'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ClipboardList,
  Loader2,
  Plus,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAddObligationToRun,
  useAssignments,
  useUpdateAssignments,
} from '@/features/playbooks/api/use-assignments';
import {
  useActivateRun,
  useCreateActivation,
} from '@/features/playbooks/api/use-playbook-activations';
import {
  useCreateTemplate,
  usePlaybookTemplates,
} from '@/features/playbooks/api/use-playbook-templates';
import { AddObligationDialogControlled } from '@/features/playbooks/components/AddObligationDialog';
import { TemplateCard } from '@/features/playbooks/components/TemplateCard';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import type {
  CreateObligationInput,
  PlaybookDomain,
  PlaybookObligationAssignment,
} from '@/features/playbooks/types';
import { useReportingSettings } from '@/features/datahub/periods/api/use-reporting-settings';
import { useActiveOrganizationId } from '@/features/tasks/hooks/use-active-organization-id';
import { useMembers } from '@/features/team/api/use-members';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

/** Resolve 'reporting_period' to the org's actual reporting granularity label */
const CADENCE_LABELS: Record<string, string> = {
  once: 'once',
  monthly: 'monthly',
  quarterly: 'quarterly',
  annually: 'annually',
  annual: 'annually',
};

function useResolveCadenceLabel(cadence: string | null | undefined): string {
  const { data: settings } = useReportingSettings();
  const raw = cadence ?? 'once';
  if (raw === 'reporting_period') {
    const granularity = settings?.reportingGranularity ?? 'monthly';
    return CADENCE_LABELS[granularity] ?? 'monthly';
  }
  return CADENCE_LABELS[raw] ?? raw;
}

export const Route = createFileRoute('/_authenticated/tasks/playbooks/activate')({
  validateSearch: (search: Record<string, unknown>) => ({
    templateId: typeof search.templateId === 'string' ? search.templateId : undefined,
  }),
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.startWorkflowRun'),
  }),
  component: ActivatePlaybookPage,
});

function ActivatePlaybookPage() {
  const { t } = useTranslation('tasks');
  const { templateId: preselectedTemplateId } = Route.useSearch();
  const navigate = useNavigate();
  const { templates, isLoading: templatesLoading } = usePlaybookTemplates();
  const { createActivation, isCreating } = useCreateActivation();
  const { activateRun, isActivating } = useActivateRun();
  const { createTemplate, isCreating: isCreatingTemplate } = useCreateTemplate();
  const { organizationId } = useActiveOrganizationId();

  const [step, setStep] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const [startMonth, setStartMonth] = useState(String(currentMonth));
  const [startYear, setStartYear] = useState(String(currentYear));
  const [endMonth, setEndMonth] = useState('12');
  const [endYear, setEndYear] = useState(String(currentYear));
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDomain, setNewTemplateDomain] = useState<PlaybookDomain>('custom');

  // Draft activation state — created after step 1
  const [draftActivationId, setDraftActivationId] = useState<string | null>(null);

  // Compute ISO date strings from month/year selectors
  const periodStart = `${startYear}-${startMonth.padStart(2, '0')}-01`;
  const periodEndDate = new Date(Number(endYear), Number(endMonth), 0); // last day of end month
  const periodEnd = `${endYear}-${endMonth.padStart(2, '0')}-${String(periodEndDate.getDate()).padStart(2, '0')}`;

  const months = [
    { value: '1', label: t('activate.months.january') },
    { value: '2', label: t('activate.months.february') },
    { value: '3', label: t('activate.months.march') },
    { value: '4', label: t('activate.months.april') },
    { value: '5', label: t('activate.months.may') },
    { value: '6', label: t('activate.months.june') },
    { value: '7', label: t('activate.months.july') },
    { value: '8', label: t('activate.months.august') },
    { value: '9', label: t('activate.months.september') },
    { value: '10', label: t('activate.months.october') },
    { value: '11', label: t('activate.months.november') },
    { value: '12', label: t('activate.months.december') },
  ];
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - 1 + i));

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const tmpl = templates.find((t) => t.id === templateId);
    if (tmpl) {
      setName(`${tmpl.name} ${String(currentYear)}`);
    }
  };

  useEffect(() => {
    if (!preselectedTemplateId || templatesLoading || selectedTemplateId) return;

    const template = templates.find((item) => item.id === preselectedTemplateId);
    if (!template) return;

    handleSelectTemplate(template.id);
    setStep(1);
  }, [preselectedTemplateId, selectedTemplateId, templates, templatesLoading]);

  const handleCreateTemplateFromChooser = async () => {
    if (!newTemplateName.trim()) return;
    try {
      const created = await createTemplate({
        name: newTemplateName.trim(),
        description: null,
        domain: newTemplateDomain,
        metadata: {},
        phases: [],
      });
      setIsTemplateDialogOpen(false);
      setNewTemplateName('');
      setNewTemplateDomain('custom');
      handleSelectTemplate(created.id);
      toast.success(t('tasks:toast.templateCreatedAndSelected'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('tasks:toast.failedToCreateTemplate'));
    }
  };

  // Create draft activation when moving from step 1 to step 2
  const handleCreateDraft = async () => {
    if (!selectedTemplateId) return;
    try {
      const activation = await createActivation({
        templateId: selectedTemplateId,
        name,
        startDate: periodStart,
        endDate: periodEnd,
      });
      setDraftActivationId(activation.id);
      setStep(2);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('tasks:toast.failedToCreateWorkflowRun'));
    }
  };

  // Activate the draft when finishing on the review step
  const handleActivate = async () => {
    if (!draftActivationId) return;
    try {
      await activateRun(draftActivationId);
      toast.success(t('tasks:toast.workflowRunStarted'));
      void navigate({
        to: '/tasks/playbooks/$activationId',
        params: { activationId: draftActivationId },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('tasks:toast.failedToStartWorkflowRun'));
    }
  };

  const steps = [
    t('activate.steps.chooseTemplate'),
    t('activate.steps.configureRunPeriod'),
    t('activate.steps.assignObligations'),
    t('activate.steps.reviewAndStart'),
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <WorkflowSectionTabs activeTab="runs" />
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => void navigate({ to: '/tasks/playbooks' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('activate.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('activate.stepOf', { current: String(step + 1), total: String(steps.length), stepName: steps[step] })}
          </p>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex gap-2">
        {steps.map((s, i) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-primary' : 'bg-muted'}`}
          />
        ))}
      </div>

      {/* Step 0: Template selection */}
      {step === 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
            <p className="text-sm text-muted-foreground">
              {t('activate.chooseTemplateHint')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsTemplateDialogOpen(true);
                }}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {t('activate.newTemplate')}
              </Button>
            </div>
          </div>
          {templatesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((tmpl) => (
                <TemplateCard
                  key={tmpl.id}
                  template={tmpl}
                  selected={selectedTemplateId === tmpl.id}
                  onClick={() => {
                    handleSelectTemplate(tmpl.id);
                  }}
                />
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setStep(1);
              }}
              disabled={!selectedTemplateId}
            >
              {t('activate.next')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 1: Period configuration */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('activate.configureActivePeriod')}</CardTitle>
            <CardDescription>
              {t('activate.configureActivePeriodDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('activate.workflowRunName')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('activate.from')}</Label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={startMonth}
                  onChange={(e) => {
                    setStartMonth(e.target.value);
                  }}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  value={startYear}
                  onChange={(e) => {
                    setStartYear(e.target.value);
                  }}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('activate.to')}</Label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={endMonth}
                  onChange={(e) => {
                    setEndMonth(e.target.value);
                  }}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  value={endYear}
                  onChange={(e) => {
                    setEndYear(e.target.value);
                  }}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setStep(0);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('activate.back')}
              </Button>
              <Button
                onClick={() => void handleCreateDraft()}
                disabled={!name || periodStart >= periodEnd || isCreating}
              >
                {isCreating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="ml-2 h-4 w-4" />
                )}
                {t('activate.next')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Assign obligations */}
      {step === 2 && draftActivationId && (
        <AssignObligationsStep
          activationId={draftActivationId}
          orgId={organizationId ?? ''}
          domain={selectedTemplate?.domain ?? 'custom'}
          onBack={() => {
            setStep(1);
          }}
          onNext={() => {
            setStep(3);
          }}
        />
      )}

      {/* Step 3: Review & Activate */}
      {step === 3 && selectedTemplate && (
        <Card>
          <CardHeader>
            <CardTitle>{t('activate.reviewAndStart')}</CardTitle>
            <CardDescription>
              {t('activate.reviewAndStartDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activate.template')}</p>
                <p className="font-medium">{selectedTemplate.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activate.domain')}</p>
                <p className="font-medium uppercase">{selectedTemplate.domain}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activate.name')}</p>
                <p className="font-medium">{name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activate.activePeriod')}</p>
                <p className="font-medium">
                  {months.find((m) => m.value === startMonth)?.label} {startYear} &ndash;{' '}
                  {months.find((m) => m.value === endMonth)?.label} {endYear}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activate.phases')}</p>
                <p className="font-medium">{String(selectedTemplate.phaseCount)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activate.obligations')}</p>
                <p className="font-medium">{String(selectedTemplate.obligationCount)}</p>
              </div>
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setStep(2);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('activate.back')}
              </Button>
              <Button onClick={() => void handleActivate()} disabled={isActivating}>
                {isActivating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                {t('activate.startWorkflowRun')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('activate.createTemplate')}</DialogTitle>
            <DialogDescription>
              {t('activate.createTemplateDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-template-name">{t('activate.templateName')}</Label>
              <Input
                id="new-template-name"
                value={newTemplateName}
                onChange={(e) => {
                  setNewTemplateName(e.target.value);
                }}
                placeholder={t('activate.templateNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-template-domain">{t('activate.domain')}</Label>
              <select
                id="new-template-domain"
                value={newTemplateDomain}
                onChange={(e) => {
                  setNewTemplateDomain(e.target.value as PlaybookDomain);
                }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="ghg">{t('templates.domainGhg')}</option>
                <option value="esg">{t('templates.domainEsg')}</option>
                <option value="nis2">{t('templates.domainNis2')}</option>
                <option value="custom">{t('templates.domainCustom')}</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsTemplateDialogOpen(false);
              }}
            >
              {t('activate.cancel')}
            </Button>
            <Button
              onClick={() => {
                void handleCreateTemplateFromChooser();
              }}
              disabled={!newTemplateName.trim() || isCreatingTemplate}
            >
              {isCreatingTemplate ? t('activate.creating') : t('activate.createTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Step 2: Assign Obligations ──

interface AssignObligationsStepProps {
  activationId: string;
  orgId: string;
  domain: PlaybookDomain;
  onBack: () => void;
  onNext: () => void;
}

function AssignObligationsStep({
  activationId,
  orgId,
  domain,
  onBack,
  onNext,
}: AssignObligationsStepProps) {
  const { t } = useTranslation('tasks');
  const { assignments, isLoading } = useAssignments(activationId);
  const { updateAssignments, isUpdating } = useUpdateAssignments(activationId);
  const { addObligation, isAdding } = useAddObligationToRun(activationId);
  const membersQuery = useMembers({ orgId, pageSize: 100 });
  const members = membersQuery.data?.members ?? [];
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Local edits keyed by assignment ID
  const [edits, setEdits] = useState<
    Record<string, { assigneeId?: string | null; priority?: string | null }>
  >({});

  const handleAssigneeChange = useCallback(
    (assignmentId: string, assigneeId: string | null) => {
      setEdits((prev) => ({
        ...prev,
        [assignmentId]: { ...prev[assignmentId], assigneeId },
      }));
    },
    []
  );

  const handlePriorityChange = useCallback(
    (assignmentId: string, priority: string) => {
      setEdits((prev) => ({
        ...prev,
        [assignmentId]: { ...prev[assignmentId], priority },
      }));
    },
    []
  );

  const handleAddObligation = async (obligation: CreateObligationInput) => {
    try {
      await addObligation(obligation);
      setIsAddDialogOpen(false);
      toast.success(t('tasks:toast.taskAddedToWorkflow'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('tasks:toast.failedToAddTask'));
    }
  };

  const handleSaveAndNext = async () => {
    // Build list of changes to send
    const updates = Object.entries(edits)
      .filter(([, v]) => v.assigneeId !== undefined || v.priority !== undefined)
      .map(([assignmentId, v]) => ({
        assignmentId,
        assigneeId: v.assigneeId ?? null,
        priority: v.priority ?? null,
      }));

    if (updates.length > 0) {
      try {
        await updateAssignments(updates);
      } catch {
        toast.error(t('tasks:toast.failedToSaveAssignmentChanges'));
        return;
      }
    }
    onNext();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('activate.assignObligations')}
              </CardTitle>
              <CardDescription>
                {assignments.length > 0
                  ? t('activate.assignObligationsDescription')
                  : t('activate.addTasksDescription')}
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(true);
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {t('activate.addTask')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {assignments.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-10">
              <ClipboardList className="h-10 w-10 text-muted-foreground/50" />
              <div className="text-center">
                <p className="text-sm font-medium">{t('activate.noTasksYet')}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('activate.noTasksYetDescription')}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setIsAddDialogOpen(true);
                }}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {t('activate.addYourFirstTask')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {assignments.map((a) => (
                <ObligationAssignmentRow
                  key={a.id}
                  assignment={a}
                  members={members}
                  editValues={edits[a.id]}
                  onAssigneeChange={(val) => {
                    handleAssigneeChange(a.id, val);
                  }}
                  onPriorityChange={(val) => {
                    handlePriorityChange(a.id, val);
                  }}
                />
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="w-full border border-dashed text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setIsAddDialogOpen(true);
                }}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {t('activate.addAnotherTask')}
              </Button>
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('activate.back')}
            </Button>
            <Button onClick={() => void handleSaveAndNext()} disabled={isUpdating}>
              {isUpdating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="ml-2 h-4 w-4" />
              )}
              {t('activate.next')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddObligationDialogControlled
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAdd={(obligation) => void handleAddObligation(obligation)}
        isAdding={isAdding}
        domain={domain}
      />
    </>
  );
}

// ── Obligation Assignment Row ──

interface MemberOption {
  id: string;
  userId: string;
  displayName: string | null;
  email: string;
}

interface ObligationAssignmentRowProps {
  assignment: PlaybookObligationAssignment;
  members: MemberOption[];
  editValues: { assigneeId?: string | null; priority?: string | null } | undefined;
  onAssigneeChange: (assigneeId: string | null) => void;
  onPriorityChange: (priority: string) => void;
}

function ObligationAssignmentRow({
  assignment,
  members,
  editValues,
  onAssigneeChange,
  onPriorityChange,
}: ObligationAssignmentRowProps) {
  const cadenceLabel = useResolveCadenceLabel(assignment.cadence);
  const currentAssignee = editValues?.assigneeId !== undefined
    ? editValues.assigneeId
    : assignment.assigneeId;
  const currentPriority = editValues?.priority ?? assignment.priority ?? 'medium';

  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{assignment.obligationName ?? i18n.t('tasks:activate.obligation')}</p>
        <p className="text-xs text-muted-foreground">
          {cadenceLabel} &middot; {assignment.regulatoryReference ?? '—'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Select
          value={currentAssignee ?? '_none'}
          onValueChange={(val) => {
            onAssigneeChange(val === '_none' ? null : val);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={i18n.t('tasks:activate.unassigned')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">{i18n.t('tasks:activate.unassigned')}</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.userId} value={m.userId}>
                {m.displayName ?? m.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={currentPriority} onValueChange={onPriorityChange}>
          <SelectTrigger className="w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">{i18n.t('tasks:priority.low')}</SelectItem>
            <SelectItem value="medium">{i18n.t('tasks:priority.medium')}</SelectItem>
            <SelectItem value="high">{i18n.t('tasks:priority.high')}</SelectItem>
            <SelectItem value="critical">{i18n.t('tasks:priority.critical')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// The AddObligationDialog is now imported from
// @/features/playbooks/components/AddObligationDialog
// (AddObligationDialogControlled variant)
